/**
 * 爱微圈导航 · 分享 / 打赏 / 关于 / 反馈
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, gateGuest, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export function shareTargetUrl() {
    return state.site?.share_url || location.origin + location.pathname;
}

export function shareTextTitle() {
    return state.site?.share_title || (state.site?.site_title || '爱微圈导航');
}

export function shareTextDesc() {
    return state.site?.share_desc || '';
}

export function shareQrSrc() {
    // 后台配置了二维码则直接用，否则用在线服务生成当前链接二维码
    return state.site?.share_qr_url || ('https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=' + encodeURIComponent(shareTargetUrl()));
}

export function bindShareCard() {
    const card = $('#shareCard');
    if (!card) return;
    // 填充后台配置的标题/文案
    const t = $('#shareCardTitle'), d = $('#shareCardDesc');
    if (t && shareTextTitle()) t.textContent = shareTextTitle();
    if (d && shareTextDesc()) d.textContent = shareTextDesc();
    const copyBtn = $('#shareCopyBtn');
    if (copyBtn) copyBtn.onclick = (e) => {
        e.stopPropagation();
        const text = shareTextDesc() ? shareTextTitle() + '\n' + shareTextDesc() + '\n' + shareTargetUrl() : shareTargetUrl();
        navigator.clipboard?.writeText(text).then(() => toast('分享内容已复制', 'success')).catch(() => toast('复制失败，请手动复制', 'error'));
    };
    const qrBtn = $('#shareQrBtn');
    if (qrBtn) qrBtn.onclick = (e) => { e.stopPropagation(); showShareModal('qr'); };
    const nativeBtn = $('#shareNativeBtn');
    if (nativeBtn) nativeBtn.onclick = async (e) => {
        e.stopPropagation();
        const data = { title: shareTextTitle(), text: shareTextDesc(), url: shareTargetUrl() };
        if (navigator.share) {
            try { await navigator.share(data); } catch (err) { /* 用户取消 */ }
        } else {
            navigator.clipboard?.writeText(data.title + '\n' + data.text + '\n' + data.url);
            toast('当前浏览器不支持系统分享，已复制链接', 'info');
        }
    };
    // 点击卡片其他区域 → 打开完整分享弹窗
    card.onclick = () => showShareModal();
}

export function bindDonateCard() {
    const d = $('#donateCardDesc');
    if (d && state.site?.donate_text) d.textContent = state.site.donate_text;
    const wx = state.site?.donate_wechat_qr || '';
    const ali = state.site?.donate_alipay_qr || '';
    const generic = state.site?.donate_qr_url || '';
    const row = $('#donateQrRow');
    // 后台勾选的展示码（最多 2 个）；未勾选时自动：聚合码 > 微信+支付宝
    let codes = [];
    String(state.site?.donate_display || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, 2)
        .forEach(k => { const u = { wechat: wx, alipay: ali, aggregate: generic }[k]; if (u) codes.push(u); });
    if (!codes.length) codes = generic ? [generic] : [wx, ali].filter(Boolean);
    codes = codes.slice(0, 2);
    if (row && codes.length) {
        row.innerHTML = codes.map(u => `<img class="donate-qr" src="${escAttr(u)}" alt="打赏二维码">`).join('');
    }
    const btn = $('#donateBtn');
    const hero = $('#donateHero');
    if (btn) btn.onclick = (e) => { e.stopPropagation(); showDonateModal(); };
    if (row) row.onclick = (e) => { e.stopPropagation(); showDonateModal(); };
    if (hero) hero.onclick = (e) => {
        // 行内按钮和 QR 缩略各自 stopPropagation，已防重复触发
        if (e.target.closest('.donate-qr-thumb') || e.target.closest('#donateBtn')) return;
        showDonateModal();
    };
}


export async function loadDonateRank(targetId = 'donateModalRankList') {
    const list = $('#' + targetId);
    if (!list) return;
    list.innerHTML = `<div class="donate-rank-empty">加载中…</div>`;
    try {
        const items = await apiGet(API.donations + '?action=list', { timeoutMs: 6000 });
        renderDonateRank(items, targetId);
    } catch (e) {
        list.innerHTML = `<div class="donate-rank-empty">排行加载失败，请稍后重试</div>`;
    }
}


export function renderDonateRank(items, targetId = 'donateModalRankList') {
    const list = $('#' + targetId);
    if (!list) return;
    if (!items || !items.length) {
        list.innerHTML = `<div class="donate-rank-empty">还没有打赏，等你来坐头把交椅 🏆</div>`;
        return;
    }
    const medals = ['🥇', '🥈', '🥉'];
    list.innerHTML = items.map((it, i) => `
        <div class="donate-rank-item">
            <span class="donate-rank-medal">${medals[i] || (i + 1)}</span>
            <span class="donate-rank-main">
                <span class="donate-rank-name">${esc(it.name)}</span>
                ${it.message ? `<span class="donate-rank-msg">${esc(it.message)}</span>` : ''}
            </span>
            <span class="donate-rank-amount">¥${Number(it.amount).toFixed(2)}</span>
            <span class="donate-rank-time">${fmtDonateTime(it.created_at)}</span>
        </div>`).join('');
}


export function fmtDonateTime(ts) {
    if (!ts) return '';
    const d = new Date(String(ts).replace(' ', 'T'));
    if (isNaN(d.getTime())) return String(ts).slice(0, 10);
    const now = new Date();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    if (d.toDateString() === now.toDateString()) return `今天 ${hh}:${mm}`;
    return `${d.getMonth() + 1}月${d.getDate()}日`;
}

export function showShareModal(focusQr) {
    const url = encodeURIComponent(shareTargetUrl());
    const title = encodeURIComponent(shareTextTitle());
    openModal(`
        <div class="modal-title">分享给好友</div>
        ${shareTextDesc() ? `<div style="font-size:12.5px;color:var(--text-dim);line-height:1.7;margin-bottom:6px">${esc(shareTextDesc())}</div>` : ''}
        <div class="share-grid">
            <div class="share-item" data-share="weibo">
                <div class="share-icon" style="background:#E6162D"><svg viewBox="0 0 24 24"><path d="M10.1 20c-4.4 0-8-2.3-8-5.3 0-1.6.9-3.4 2.5-5C7 7.5 9.9 6.4 12 6.9c1 .2 1.6.9 1.6 1.7 0 .2 0 .4-.1.7-.1.3.3.2.3.2 2.2-.5 4.1-.3 5 .7.5.5.7 1.2.6 2 1.4.6 2.4 1.6 2.4 3 0 2.9-3.6 4.8-11.7 4.8z"/></svg></div>
                <span class="share-label">微博</span>
            </div>
            <div class="share-item" data-share="qzone">
                <div class="share-icon" style="background:#FFD400"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#1E88E5"/><text x="12" y="16" text-anchor="middle" fill="#fff" font-size="10" font-weight="bold">QZ</text></svg></div>
                <span class="share-label">QQ空间</span>
            </div>
            <div class="share-item" data-share="douban">
                <div class="share-icon" style="background:#2E963F"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#2E963F"/><text x="12" y="16" text-anchor="middle" fill="#fff" font-size="9" font-weight="bold">豆</text></svg></div>
                <span class="share-label">豆瓣</span>
            </div>
            <div class="share-item" data-share="copy">
                <div class="share-icon" style="background:#666"><svg viewBox="0 0 24 24"><path d="M16 1H4a2 2 0 00-2 2v14h2V3h12V1zm3 4H8a2 2 0 00-2 2v14a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2zm0 16H8V7h11v14z" fill="#fff"/></svg></div>
                <span class="share-label">复制链接</span>
            </div>
        </div>
        <div class="share-qr-wrap">
            <img class="share-qr-img" id="shareQrImg" src="${escAttr(shareQrSrc())}" alt="站点二维码">
            <div class="share-qr-hint">扫一扫，在手机上打开</div>
        </div>
        <div class="modal-actions" style="margin-top:14px"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
    $$('.share-item').forEach(item => {
        item.onclick = () => {
            const type = item.dataset.share;
            if (type === 'copy') {
                navigator.clipboard?.writeText(shareTargetUrl()).then(() => toast('链接已复制', 'success'));
            } else if (type === 'weibo') {
                window.open('https://service.weibo.com/share/share.php?url=' + url + '&title=' + title, '_blank', 'width=600,height=500');
            } else if (type === 'qzone') {
                window.open('https://sns.qzone.qq.com/cgi-bin/qzshare/cgi_qzshare_onekey?url=' + url + '&title=' + title, '_blank', 'width=600,height=500');
            } else if (type === 'douban') {
                window.open('https://www.douban.com/share/service?href=' + url + '&name=' + title, '_blank', 'width=600,height=500');
            }
        };
    });
}


export function showFeedbackModal() {
    if (gateGuest()) return; // 意见反馈属登录限定（2026-09-04 策略）
    openModal(`
        <div class="modal-title">意见反馈</div>
        <div class="modal-error" id="fbErr"></div>
        <div class="form-group"><label>反馈类型</label>
            <select class="form-input" id="fbType">
                <option value="bug">问题反馈</option>
                <option value="feature">功能建议</option>
                <option value="other">其他</option>
            </select>
        </div>
        <div class="form-group"><label>反馈内容</label><textarea class="form-input" id="fbContent" rows="4" placeholder="请描述您遇到的问题或建议..." style="resize:vertical"></textarea></div>
        <div class="form-group"><label>联系方式（选填）</label><input type="text" class="form-input" id="fbContact" placeholder="邮箱或手机，方便我们回复您"></div>
        <button class="btn btn-primary btn-block" id="fbSubmit">提交反馈</button>
        <div class="modal-actions" style="margin-top:12px"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
    $('#fbSubmit').onclick = async () => {
        const content = $('#fbContent').value.trim();
        if (!content) { showModalError('#fbErr', '请填写反馈内容'); return; }
        const btn = $('#fbSubmit');
        btn.disabled = true;
        btn.textContent = '提交中…';
        try {
            await apiPost(API.feedback, {
                type: $('#fbType').value,
                content: content,
                contact: $('#fbContact').value.trim(),
            });
            toast('感谢您的反馈，我们会尽快处理', 'success');
            closeModal();
        } catch (e) {
            btn.disabled = false;
            btn.textContent = '提交反馈';
            showModalError('#fbErr', e.message || '提交失败，请稍后重试');
        }
    };
}


export function detectPayApp() {
    const ua = navigator.userAgent || '';
    if (/MicroMessenger/i.test(ua)) return 'wechat';
    if (/AlipayClient/i.test(ua)) return 'alipay';
    return '';
}


export function showDonateModal() {
    const text = state.site?.donate_text || '如果您喜欢本站，可以请我喝杯咖啡，您的支持是我们持续改进的动力';
    const wx = state.site?.donate_wechat_qr || '';
    const ali = state.site?.donate_alipay_qr || '';
    const generic = state.site?.donate_qr_url || '';
    const env = detectPayApp();

    // 组装二维码：后台勾选的码优先（最多 2 个）；未勾选时自动按上传情况（聚合码 > 微信+支付宝）
    const pool = {
        wechat:    { label: '微信',   url: wx,      cls: 'wx'  },
        alipay:    { label: '支付宝', url: ali,     cls: 'ali' },
        aggregate: { label: '聚合码', url: generic, cls: 'uni' },
    };
    let qrs = [];
    String(state.site?.donate_display || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, 2)
        .forEach(k => { if (pool[k] && pool[k].url) qrs.push(pool[k]); });
    if (!qrs.length) {
        if (generic) qrs.push(pool.aggregate);
        else {
            if (wx) qrs.push(pool.wechat);
            if (ali) qrs.push(pool.alipay);
        }
    }
    qrs = qrs.slice(0, 2);

    // 智能单码切换：在微信/支付宝 App 内锁定对应码（若在展示列表中），
    // 普通浏览器默认第一张，顶部提供切换。
    let activeCls = '';
    if (env === 'wechat') activeCls = 'wx';
    else if (env === 'alipay') activeCls = 'ali';
    if (!qrs.some(q => q.cls === activeCls)) activeCls = qrs.length ? qrs[0].cls : '';

    let qrHtml;
    if (!qrs.length) {
        qrHtml = `<div class="donate-modal-qr donate-modal-qr-empty"><span>🍵</span>暂未配置收款码</div>`;
    } else if (qrs.length === 1) {
        qrHtml = `<img src="${escAttr(qrs[0].url)}" alt="${escAttr(qrs[0].label || '收款码')}" class="donate-modal-qr">`;
    } else {
        // 多码：单码展示 + 切换 tab（仅普通浏览器需要切换；App 内已被 active 锁定）
        const needSwitch = !env;
        qrHtml = `
            ${needSwitch ? `<div class="donate-switch" id="donateSwitch">` + qrs.map(q => `
                <button type="button" class="donate-switch-btn ${q.cls}${q.cls === activeCls ? ' active' : ''}" data-cls="${q.cls}">${esc(q.label)}</button>`).join('') + `</div>` : ''}
            <div class="donate-qr-stage">` + qrs.map(q => `
                <img src="${escAttr(q.url)}" alt="${escAttr(q.label || '收款码')}" data-cls="${q.cls}" class="donate-modal-qr donate-qr-panel" style="${q.cls === activeCls ? '' : 'display:none'}">`).join('') + `
            </div>`;
    }

    const hint = qrs.length > 1
        ? ((env === 'wechat' && activeCls === 'wx') ? '已识别到微信，请用微信扫码打赏'
           : (env === 'alipay' && activeCls === 'ali') ? '已识别到支付宝，请用支付宝扫码打赏'
           : '点击上方切换收款码，选一个扫码即可')
        : '扫码打赏 · 微信 / 支付宝均可';

    openModal(`
        <div class="donate-modal-head">
            <div class="donate-modal-emoji">🧧</div>
            <div class="donate-modal-title">打赏</div>
            <div class="donate-modal-sub">${esc(text)}</div>
        </div>
        <div class="donate-modal-body">
            <div class="donate-modal-qr-wrap">${qrHtml}</div>
            <div class="donate-modal-hint">${esc(hint)}</div>
            <div class="donate-modal-divider" style="margin-top:18px"><span>🏆 打赏排行</span></div>
            <div class="donate-modal-rank" id="donateModalRankList">
                <div class="donate-rank-empty">加载中…</div>
            </div>
        </div>
        <div class="modal-actions"><button class="btn btn-light" data-close-modal>关闭</button></div>`);

    // 切换逻辑：点 tab 切换展示的二维码
    const sw = document.getElementById('donateSwitch');
    if (sw) {
        sw.querySelectorAll('.donate-switch-btn').forEach(btn => {
            btn.addEventListener('click', () => {
                const cls = btn.dataset.cls;
                sw.querySelectorAll('.donate-switch-btn').forEach(b => b.classList.toggle('active', b === btn));
                document.querySelectorAll('.donate-qr-panel').forEach(img => {
                    img.style.display = img.dataset.cls === cls ? '' : 'none';
                });
            });
        });
    }

    // 加载排行
    loadDonateRank('donateModalRankList');
}


export function showAboutModal() {
    openModal(`
        <div class="about-content">
            <div class="about-logo"><svg viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg></div>
            <div class="about-name">${esc(state.site?.site_title || '爱微圈导航')}</div>
            <div class="about-version">${esc(state.site?.site_version || 'v1.0.0')}</div>
            <div class="about-desc">
                ${esc(state.site?.site_subtitle || '发现精彩内容，定制你的专属上网导航')}<br><br>
                一款简洁、美观、可定制的个人导航主页。支持快捷方式管理、云同步、待办事项、便签笔记、天气、多引擎搜索、壁纸市场等功能。
            </div>
            <div class="about-links">
                <a href="#" id="aboutContact">联系我们</a>
                <a href="#" id="aboutPrivacy">隐私政策</a>
                <a href="#" id="aboutTerms">服务条款</a>
            </div>
        </div>
        <div class="modal-actions" style="margin-top:16px"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
    $('#aboutContact').onclick = (e) => { e.preventDefault(); closeModal(); showContactModal(); };
    $('#aboutPrivacy').onclick = (e) => { e.preventDefault(); closeModal(); showPrivacyModal(); };
    $('#aboutTerms').onclick = (e) => { e.preventDefault(); closeModal(); showTermsModal(); };
}


export function showContactModal() {
    openModal(`
        <div class="modal-title">联系我们</div>
        <div style="font-size:13px;color:var(--text-dim);line-height:2;padding:12px 0">
            📧 邮箱：support@example.com<br>
            💬 QQ群：123456789<br>
            🐛 问题反馈：请使用「意见反馈」功能<br>
            ⏰ 工作时间：周一至周五 9:00-18:00
        </div>
        <div class="modal-actions"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
}


export function showPrivacyModal() {
    openModal(`
        <div class="modal-title">隐私政策</div>
        <div style="font-size:13px;color:var(--text-dim);line-height:1.8;max-height:400px;overflow-y:auto;padding:8px 0">
            <p>1. 我们仅收集必要的账户信息（邮箱、昵称）用于提供服务。</p>
            <p>2. 您的快捷方式、待办、便签等数据归您所有，不会用于其他用途。</p>
            <p>3. 密码使用 bcrypt 加密存储，我们无法查看您的明文密码。</p>
            <p>4. 我们不会向第三方出售或分享您的个人信息。</p>
            <p>5. 您可以随时导出或删除您的全部数据。</p>
            <p>6. 本服务使用 Cookie 和本地存储来保存登录状态和偏好设置。</p>
        </div>
        <div class="modal-actions"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
}


export function showTermsModal() {
    openModal(`
        <div class="modal-title">服务条款</div>
        <div style="font-size:13px;color:var(--text-dim);line-height:1.8;max-height:400px;overflow-y:auto;padding:8px 0">
            <p>1. 本服务为个人导航工具，您需对使用本服务的行为负责。</p>
            <p>2. 请勿利用本服务进行任何违法违规活动。</p>
            <p>3. 我们保留随时修改或终止服务的权利。</p>
            <p>4. 服务按"现状"提供，不做任何明示或暗示的保证。</p>
            <p>5. 继续使用本服务即表示您同意本条款。</p>
        </div>
        <div class="modal-actions"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
}

/* ---------- 缓存（快速首屏，stale-while-revalidate） ---------- */
/* 本地缓存只负责"首屏不空白"：无论新旧都先立即渲染，随后 loadShortcuts/loadSettings
 * 必然用服务器数据覆盖（权威在服务端）。因此不再使用短 TTL 整体丢弃——
 * 旧的 5 分钟硬过期正是"重新打开先空白再出现"的根因。仅保留 7 天兜底防远古脏数据。 */

